import { useState, useCallback } from "react";
import { ConnectStoreForm } from './ConnectStoreForm';
import { BillingStoreSelector } from './BillingStoreSelector';

const TIERS = [
  { tier: 'starter', name: 'Starter', monthly: '49', annual: '470', runs: '50', stores: '3', trialDays: 5 },
  { tier: 'pro', name: 'Pro', monthly: '99', annual: '950', runs: '200', stores: '10', popular: true, trialDays: 5 },
  { tier: 'agency', name: 'Agency', monthly: '199', annual: '1,900', runs: '800', stores: '30', trialDays: 5 },
  { tier: 'unlimited', name: 'Unlimited', monthly: '399', annual: '3,800', runs: 'Unlimited', stores: 'Unlimited', trialDays: 5 },
];

// ═══════ Billing / Upgrade Modal ═══════
export const BillingModal = ({
  showUpgradeModal, setShowUpgradeModal, handleUpgrade, upgradeLoading, usage, billingError, billingErrorCode, onStoreChange,
}) => {
  const [selectedInterval, setSelectedInterval] = useState('monthly');
  const [selectedShop, setSelectedShop] = useState(usage?.oauth_store_domain || '');
  const [storeData, setStoreData] = useState(null);

  const onStoresLoaded = useCallback((d) => {
    setStoreData(d);
    setSelectedShop(prev => prev || d.billing_store_domain || d.stores?.[0]?.shop_domain || '');
    onStoreChange?.();
  }, [onStoreChange]);

  if (!showUpgradeModal) return null;

  const currentStore = storeData?.stores?.find(s => s.shop_domain === selectedShop);
  const currentPlan = currentStore ? currentStore.plan : usage?.plan;

  return (
    <div className="upgrade-modal-overlay" data-testid="upgrade-modal">
      <div className="upgrade-modal">
        <div className="upgrade-modal-header">
          <h2 style={{fontSize:'18px',fontWeight:700}}>Choose Your Plan{selectedShop ? <span style={{fontSize:'12px',fontWeight:500,color:'var(--text-muted)',marginLeft:'8px'}} data-testid="plan-for-store">for {selectedShop}</span> : null}</h2>
          <button className="beige-btn" onClick={() => setShowUpgradeModal(false)} data-testid="close-upgrade-modal" style={{padding:'4px 10px'}}>
            <svg style={{width:'16px',height:'16px'}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div style={{margin:'0 16px'}}>
          <BillingStoreSelector open={showUpgradeModal} selectedShop={selectedShop} onSelect={setSelectedShop} onStoresLoaded={onStoresLoaded} />
        </div>
        {billingError && (
          <div role="alert" data-testid="billing-error" style={{margin:'8px 16px 0',padding:'10px 12px',background:'rgba(200,170,160,0.3)',border:'1px solid #c4a69a',borderRadius:'6px',fontSize:'12px',color:'var(--accent-red)'}}>
            {billingError}
            {billingErrorCode === 'no_oauth_session' && <div style={{marginTop:'8px'}}><ConnectStoreForm defaultShop={usage?.store_domain || ''} compact /></div>}
          </div>
        )}
        <div style={{display:'flex',justifyContent:'center',gap:'8px',padding:'12px 16px'}}>
          <button className={`beige-btn ${selectedInterval === 'monthly' ? 'beige-btn-red' : ''}`} onClick={() => setSelectedInterval('monthly')} data-testid="interval-monthly" style={{fontSize:'12px'}}>Monthly</button>
          <button className={`beige-btn ${selectedInterval === 'annual' ? 'beige-btn-red' : ''}`} onClick={() => setSelectedInterval('annual')} data-testid="interval-annual" style={{fontSize:'12px'}}>Annual (save ~20%)</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',padding:'8px 16px 16px'}}>
          {TIERS.map(t => (
            <div key={t.tier} className={`tier-card ${t.popular ? 'tier-card-popular' : ''}`} data-testid={`tier-card-${t.tier}`}>
              {t.popular && <div style={{fontSize:'10px',fontWeight:600,color:'var(--accent-red)',marginBottom:'4px'}}>MOST POPULAR</div>}
              <h3 style={{fontSize:'16px',fontWeight:700,marginBottom:'4px'}}>{t.name}</h3>
              <div style={{marginBottom:'8px'}}>
                <span style={{fontSize:'22px',fontWeight:700}}>{selectedInterval === 'annual' ? t.annual : t.monthly}</span>
                <span style={{fontSize:'12px',color:'var(--text-muted)',marginLeft:'4px'}}>EUR/{selectedInterval === 'annual' ? 'yr' : 'mo'}</span>
              </div>
              <div style={{fontSize:'12px',color:'var(--text-secondary)',marginBottom:'10px'}}>
                <div>{t.runs} runs/month</div>
                <div>{t.stores} stores</div>
                <div>No watermark</div>
                {selectedInterval === 'monthly' && t.trialDays > 0 && (
                  <div style={{color:'var(--accent-red)',fontWeight:600}}>{t.trialDays}-day free trial</div>
                )}
              </div>
              <button className="beige-btn beige-btn-full" onClick={() => handleUpgrade(t.tier, selectedInterval, selectedShop)} disabled={!selectedShop || upgradeLoading === t.tier || currentPlan === t.tier} data-testid={`select-tier-${t.tier}`}>
                {upgradeLoading === t.tier ? 'Redirecting...' : currentPlan === t.tier ? 'Current' : 'Select'}
              </button>
            </div>
          ))}
        </div>
        <div style={{padding:'0 16px 16px'}}>
          <div className="tier-card" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}} data-testid="tier-card-lifetime">
            <div>
              <h3 style={{fontSize:'15px',fontWeight:700}}>Lifetime</h3>
              <div style={{fontSize:'12px',color:'var(--text-secondary)'}}>One-time, unlimited forever</div>
            </div>
            <div style={{fontSize:'20px',fontWeight:700}}>2,490 EUR</div>
            <button className="beige-btn beige-btn-red" onClick={() => handleUpgrade('lifetime', 'monthly', selectedShop)} disabled={!selectedShop || upgradeLoading === 'lifetime' || currentPlan === 'lifetime'} data-testid="select-tier-lifetime" style={{marginLeft:'12px'}}>
              {upgradeLoading === 'lifetime' ? 'Redirecting...' : currentPlan === 'lifetime' ? 'Current' : 'Get Lifetime'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
